import { collection, query, orderBy, startAt, endAt, getDocs, updateDoc, doc, where, limit, onSnapshot } from 'firebase/firestore';
import * as geofire from 'geofire-common';
import { db } from '../firebase';

// 🎯 REGION CONFIG: Map Geohash 1st char to File
const REGION_MAP = {
    // East Asia (Korea, Japan, China East, Russia East)
    'w': 'asia_east.json', 'y': 'asia_east.json',

    // Asia-Pacific (SE Asia, Australia, NZ, India)
    'q': 'asia_south_pacific.json', 'r': 'asia_south_pacific.json',
    't': 'asia_south_pacific.json', // India/Pakistan area

    // North America (USA, Canada, Alaska)
    '8': 'north_america.json', '9': 'north_america.json',
    'b': 'north_america.json', 'c': 'north_america.json',
    'd': 'north_america.json', 'f': 'north_america.json',

    // Europe (UK, France, Germany, Italy, Russia West)
    'g': 'europe.json', 'u': 'europe.json', 'v': 'europe.json',

    // South America (Brazil, Argentina, etc.)
    '6': 'south_america.json', '7': 'south_america.json',

    // Africa & Middle East (Broad mapping)
    // Note: 's', 'k', 'e' can overlap Europe/Asia edges, but majority is Africa/ME
    's': 'africa_middle_east.json', // Middle East / East Africa
    'k': 'africa_middle_east.json', // South Africa 
    'e': 'africa_middle_east.json', // Central/West Africa 
};

// 🎯 MEMORY CACHE
const _loadedRegions = new Set();
const _staticLandmarks = [];

const formatDistance = (meters) => {
    return meters < 1000 ? `${Math.round(meters)}m` : `${(meters / 1000).toFixed(1)}km`;
};

const shouldUseLegacyFallback = () => {
    try {
        if (process.env.NODE_ENV === 'development') return true;
        if (typeof window !== 'undefined') {
            const qs = new URLSearchParams(window.location.search);
            if (qs.get('legacy_spot_fallback') === '1') return true;
        }
        if (typeof localStorage !== 'undefined' && localStorage.getItem('legacy_spot_fallback') === '1') {
            return true;
        }
    } catch (e) {
        // ignore
    }
    return false;
};

// 🎯 [Helper] Environment-aware Collection Path
const getCollectionPath = (base, env) => {
    if (!env || env === 'production' || env === 'mainnet') return base;
    // 🎯 [Revert] Sandbox uses root collections (user feedback: data exists in root)
    if (env === 'sandbox') return base;
    if (env === 'testnet') return `testnet_${base}`;
    return `dev_${base}`;
};

export const LocationService = {
    // 🎯 Static Cache for Landmarks (Memory Only)
    _staticLandmarks: [],
    _loadedRegions: new Set(),

    /**
     * Calculates the Geohash for a given location.
     * @param {number} lat Latitude
     * @param {number} lng Longitude
     * @returns {string} Geohash string
     */
    getGeohash: (lat, lng) => {
        return geofire.geohashForLocation([lat, lng]);
    },

    /**
     * Lazy Load Static Data based on User's Location
     */
    _loadRegionData: async (centerGeohash) => {
        const prefix = centerGeohash.charAt(0);
        const fileName = REGION_MAP[prefix];

        if (fileName && !LocationService._loadedRegions.has(fileName)) {
            console.log(`[LocationService] Loading Region File: ${fileName}...`);
            try {
                const response = await fetch(`/data/landmarks/${fileName}`);
                if (response.ok) {
                    const data = await response.json();
                    LocationService._staticLandmarks.push(...data);
                    LocationService._loadedRegions.add(fileName);
                    console.log(`[LocationService] Loaded ${data.length} landmarks from ${fileName}`);
                } else {
                    console.warn(`[LocationService] Failed to load ${fileName}: ${response.status}`);
                }
            } catch (e) {
                console.error(`[LocationService] Error loading region file:`, e);
            }
        }
    },

    /**
     * Fetches spots within a given radius from Firestore and Static Data.
     * Uses Geohash for efficient Firestore querying.
     * @param {object} center { lat, lng }
     * @param {number} radiusInMeters Search radius in meters
     * @param {boolean} forceLegacy If true, bypass Geohash and fetch all (Debug/Failsafe)
     * @param {string} environment 'production' | 'testnet' | 'dev'
     * @returns {Promise<Array>} List of combined spots (Firestore + Static) with distance info
     */
    getSpotsInRadius: async (center, radiusInMeters, forceLegacy = false, environment) => {
        try {
            const centerArr = [center.lat, center.lng];
            // 🎯 [Fix] Geohash Precision Buffer
            // Geohash boxes are rectangular. Use buffer to ensure strict circular coverage overlap.
            // ⚠️ [CRITICAL FIX] geofire.geohashQueryBounds expects radius in METERS, not km!
            const bufferMultiplier = 1.2; // 20% buffer for safety (50km → 60km)
            const radiusWithBuffer = radiusInMeters * bufferMultiplier; // METERS!

            // 0. Ensure Static Data is Loaded
            const centerHash = geofire.geohashForLocation(centerArr);
            await LocationService._loadRegionData(centerHash);

            const spotsColName = getCollectionPath('spots', environment);

            if (forceLegacy) {
                console.log("[LocationService] Force Legacy Mode Active");
                const legacySpots = await LocationService._getSpotsLegacy(center, radiusInMeters, environment);
                // Merge landmarks
                const legacyWithLandmarks = [...legacySpots];
                LocationService._staticLandmarks.forEach((lm) => {
                    const dM = geofire.distanceBetween([lm.geo.lat, lm.geo.lng], centerArr) * 1000;
                    if (dM <= radiusInMeters) legacyWithLandmarks.push({ ...lm, distanceValue: dM, distance: formatDistance(dM) });
                });
                return legacyWithLandmarks.sort((a, b) => a.distanceValue - b.distanceValue);
            }

            // 1. Calculate Geohash Query Bounds (radius in METERS)
            const bounds = geofire.geohashQueryBounds(centerArr, radiusWithBuffer);
            console.log(`[LocationService] Querying Radius: ${radiusWithBuffer}m (${radiusWithBuffer / 1000}km), Center:`, centerArr);
            console.log(`[LocationService] Bounds:`, JSON.stringify(bounds));

            const promises = [];

            // 2. Create Firestore Queries for each bound
            for (const b of bounds) {
                const q = query(
                    collection(db, spotsColName),
                    where('status', '==', 'active'), // 🎯 [Fix] Re-enable status filter to significantly reduce read costs
                    orderBy('geohash'),
                    startAt(b[0]),
                    endAt(b[1])
                );
                promises.push(getDocs(q));
            }

            // 3. Execute Queries Parallelly
            const snapshots = await Promise.all(promises);
            const spotsMap = new Map(); // 🎯 [Fix] Deduplication using Map

            // 4. Process Results & Filter by Distance (Client-side)
            let totalFetched = 0;
            const now = new Date(); // Capture time once for loop

            for (const snap of snapshots) {
                totalFetched += snap.docs.length;
                for (const doc of snap.docs) {
                    const data = doc.data();

                    // 🎯 [Lifecycle] Archived/Deleted spots are hidden
                    if (data.status === 'archived' || data.status === 'deleted') {
                        continue;
                    }

                    // 🎯 [Lifecycle] Lazy Expiration Check (Policy: 30 days)
                    let expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt ? new Date(data.expiresAt) : null);

                    // Fallback for old data without expiresAt: createdAt + 30 days
                    if (!expiresAt && data.createdAt) {
                        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
                        expiresAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
                    }

                    if (expiresAt && expiresAt < now) {
                        continue; // Skip Expired Spot
                    }

                    if (data.geo) {
                        // 🎯 [Fix] Support Firestore GeoPoint {latitude, longitude} and Legacy Map {lat, lng}
                        const rawLat = data.geo.lat ?? data.geo.latitude ?? data.geo._lat;
                        const rawLng = data.geo.lng ?? data.geo.longitude ?? data.geo._long;

                        const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
                        const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);

                        if (!isNaN(lat) && !isNaN(lng)) {
                            const distanceInKm = geofire.distanceBetween([lat, lng], centerArr);
                            const distanceInMeters = distanceInKm * 1000;

                            if (distanceInMeters <= radiusInMeters) {
                                spotsMap.set(doc.id, {
                                    id: doc.id,
                                    ...data,
                                    // 🎯 [Standardized] Use userName (camelCase) only
                                    userName: data.userName,
                                    distanceValue: distanceInMeters,
                                    distance: formatDistance(distanceInMeters),
                                    // 🎯 [UI] Add formatted date strings
                                    createdAtStr: data.createdAt?.toDate ? data.createdAt.toDate().toLocaleDateString() : '',
                                    // 🎯 [Normalization] Force overwrite geo with safe plain object
                                    geo: { lat, lng }
                                });
                            }
                        }
                    }
                }
            } // 🎯 Close for (snap of snapshots)
            console.log(`[LocationService] Fetched ${totalFetched} docs from Firestore. Valid in Radius: ${spotsMap.size}`);

            // 5. Integrate Static Landmarks (In-Memory Filter from CACHE)
            LocationService._staticLandmarks.forEach((lm) => {
                // 🎯 [Fix] Prevent Static Data from overwriting Firestore Data (Dynamic First)
                if (spotsMap.has(lm.id)) return;

                const distanceInKm = geofire.distanceBetween([lm.geo.lat, lm.geo.lng], centerArr);
                const distanceInMeters = distanceInKm * 1000;

                if (distanceInMeters <= radiusInMeters) {
                    spotsMap.set(lm.id, {
                        ...lm,
                        distanceValue: distanceInMeters,
                        distance: formatDistance(distanceInMeters)
                    });
                }
            });

            // 6. Final Sort by Distance
            const finalSpots = Array.from(spotsMap.values()).sort((a, b) => a.distanceValue - b.distanceValue);

            // 🎯 FAILSAFE(디버그 전용): 운영에서는 Full Scan을 막아 read 폭증 방지
            if (shouldUseLegacyFallback() && finalSpots.filter(s => s.type !== 'landmark').length === 0) {
                console.warn("[LocationService] Geohash returned 0 results. Switch to LEGACY MODE (Fetch All) to check DB...");

                const legacySpots = await LocationService._getSpotsLegacy(center, radiusInMeters, environment);

                if (legacySpots.length > 0) {
                    console.log(`[LocationService] LEGACY MODE FOUND ${legacySpots.length} items! (Geohash Query Issue detected)`);

                    // 🎯 [Fix] Deduplicate Merge for Legacy Mode
                    const legacyMap = new Map();
                    legacySpots.forEach(s => legacyMap.set(s.id, s));

                    LocationService._staticLandmarks.forEach((lm) => {
                        // Prevent overwriting DB data with Static data
                        if (legacyMap.has(lm.id)) return;

                        const dM = geofire.distanceBetween([lm.geo.lat, lm.geo.lng], centerArr) * 1000;
                        if (dM <= radiusInMeters) {
                            legacyMap.set(lm.id, { ...lm, distanceValue: dM, distance: formatDistance(dM) });
                        }
                    });

                    return Array.from(legacyMap.values()).sort((a, b) => a.distanceValue - b.distanceValue);
                }
            }
            return finalSpots;
        } catch (e) {
            console.error("LocationService Error:", e);
            // 🎯 Index Missing Error Check
            if (e.code === 'failed-precondition' || e.message.includes('index')) {
                alert("시스템 설정이 필요합니다.\n브라우저 콘솔(F12)을 열어 Firestore Index 생성 링크를 클릭해주세요.\n(Geohash order requires composite index)");
            }
            throw e;
        }
    },

    /**
     * 🎯 [NEW] Real-time Subscription for Spots
     * Uses onSnapshot instead of getDocs for real-time updates.
     * @param {object} center { lat, lng }
     * @param {number} radiusInMeters Search radius in meters
     * @param {string} environment 'production' | 'testnet' | 'dev'
     * @param {object} callbacks { onInitialLoad, onUpdate, onError }
     * @returns {function} Unsubscribe function
     */
    subscribeToSpotsInRadius: (center, radiusInMeters, environment, callbacks) => {
        const { onInitialLoad, onUpdate, onError } = callbacks;

        const centerArr = [center.lat, center.lng];
        const bufferMultiplier = 1.2;
        const radiusWithBuffer = radiusInMeters * bufferMultiplier;

        const centerHash = geofire.geohashForLocation(centerArr);
        const bounds = geofire.geohashQueryBounds(centerArr, radiusWithBuffer);
        const spotsColName = getCollectionPath('spots', environment);

        console.log(`[LocationService] 🔴 Starting onSnapshot subscription for ${bounds.length} bounds`);

        // 트래킹: 초기 로드 완료 여부
        let initialLoadComplete = false;
        const initialSpotsMap = new Map();
        let completedQueries = 0;
        let landmarksLoaded = false;

        // 헬퍼: 스팟 데이터 처리 (거리 계산, 필터링)
        const processSpotData = (docId, data) => {
            // 🎯 [Lifecycle] Archived/Deleted 제외
            if (data.status === 'archived' || data.status === 'deleted') {
                return null;
            }

            // 🎯 [Lifecycle] 만료 체크
            const now = new Date();
            let expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() :
                (data.expiresAt ? new Date(data.expiresAt) : null);

            if (!expiresAt && data.createdAt) {
                const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
                expiresAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
            }

            if (expiresAt && expiresAt < now) {
                return null; // 만료됨
            }

            // 거리 계산
            if (!data.geo) return null;

            // 🎯 [Fix] Support Firestore GeoPoint {latitude, longitude} and Legacy Map {lat, lng}
            // 🎯 [Normalization] Convert any format to plain object { lat, lng } to avoid prototype issues
            const rawLat = data.geo.lat ?? data.geo.latitude ?? data.geo._lat;
            const rawLng = data.geo.lng ?? data.geo.longitude ?? data.geo._long;

            // Validate numbers
            const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
            const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);

            if (isNaN(lat) || isNaN(lng)) return null;

            const distanceInKm = geofire.distanceBetween([lat, lng], centerArr);
            const distanceInMeters = distanceInKm * 1000;

            if (distanceInMeters > radiusInMeters) {
                return null; // 범위 초과
            }

            return {
                id: docId,
                ...data,
                // 🎯 [Standardized] Use userName (camelCase) only
                userName: data.userName,
                distanceValue: distanceInMeters,
                distance: formatDistance(distanceInMeters),
                createdAtStr: data.createdAt?.toDate ? data.createdAt.toDate().toLocaleDateString() : '',
                // 🎯 [Normalization] Force overwrite geo with safe plain object
                geo: { lat, lng }
            };
        };

        // 초기 로드 완료 체크
        const checkInitialLoad = () => {
            if (completedQueries === bounds.length && landmarksLoaded) {
                initialLoadComplete = true;
                const spotsArray = Array.from(initialSpotsMap.values());
                // 거리순 정렬 (선택사항)
                spotsArray.sort((a, b) => (a.distanceValue || 0) - (b.distanceValue || 0));

                console.log(`[LocationService] ✅ Initial load complete: ${spotsArray.length} spots (Landmarks included)`);
                onInitialLoad(spotsArray);
            }
        };

        // 🎯 랜드마크 로드 (1회)
        LocationService._loadRegionData(centerHash).then(() => {
            landmarksLoaded = true;
            if (LocationService._staticLandmarks) {
                LocationService._staticLandmarks.forEach(item => {
                    const spot = processSpotData(item.id, item);
                    if (spot) {
                        initialSpotsMap.set(item.id, spot);
                    }
                });
            }
            checkInitialLoad();
        }).catch(err => {
            console.warn('[LocationService] Failed to load landmarks:', err);
            landmarksLoaded = true; // 실패해도 진행
            checkInitialLoad();
        });

        // 각 bound에 대해 onSnapshot 구독
        const unsubscribes = bounds.map(([start, end], index) => {
            const q = query(
                collection(db, spotsColName),
                where('status', '==', 'active'), // 🎯 [Fix] Re-enable status filter for real-time listener to prevent huge cache invalidation hits
                orderBy('geohash'),
                startAt(start),
                endAt(end)
            );

            return onSnapshot(q, (snapshot) => {
                if (!initialLoadComplete) {
                    // 초기 로드 중: 모든 docs 수집
                    snapshot.docs.forEach(doc => {
                        const spot = processSpotData(doc.id, doc.data());
                        if (spot) {
                            initialSpotsMap.set(doc.id, spot);
                        }
                    });

                    completedQueries++;
                    checkInitialLoad();
                } else {
                    // 초기 로드 후: 변경분만 처리
                    const changes = [];

                    snapshot.docChanges().forEach((change) => {
                        const spot = processSpotData(change.doc.id, change.doc.data());

                        if (change.type === 'added' && spot) {
                            changes.push({ type: 'add', spot });
                        } else if (change.type === 'modified') {
                            if (spot) {
                                changes.push({ type: 'modify', spot });
                            } else {
                                // 수정 후 필터링됨 (만료, 범위 이탈 등)
                                changes.push({ type: 'remove', spotId: change.doc.id });
                            }
                        } else if (change.type === 'removed') {
                            changes.push({ type: 'remove', spotId: change.doc.id });
                        }
                    });

                    if (changes.length > 0) {
                        console.log(`[LocationService] 📡 Realtime update: ${changes.length} changes`);
                        onUpdate(changes);
                    }
                }
            }, (error) => {
                console.error('[LocationService] onSnapshot error:', error);
                if (onError) onError(error);
                // 에러 발생 시에도 완료 처리하여 무한 대기 방지
                completedQueries++;
                checkInitialLoad();
            });
        });

        // 구독 해제 함수 반환
        return () => {
            console.log('[LocationService] 🔴 Unsubscribing from all bounds');
            unsubscribes.forEach(unsub => unsub());
        };
    },

    /**
     * INTERNAL DEBUG: Legacy Fetch All Method
     * This confirms if data exists but geohash query is missing it.
     */
    _getSpotsLegacy: async (center, radiusInMeters, environment) => {
        try {
            const centerArr = [center.lat, center.lng];
            const spotsColName = getCollectionPath('spots', environment);
            // 🎯 [Safe] Limit legacy fetch to 1000 items (Newest First) to cover wider range while preventing crash
            const q = query(collection(db, spotsColName), orderBy('createdAt', 'desc'), limit(1000));
            const snapshot = await getDocs(q);
            const results = [];
            const now = new Date();

            snapshot.forEach((doc) => {
                const data = doc.data();

                // 🎯 [Lifecycle] Archived/Deleted spots are hidden
                if (data.status === 'archived' || data.status === 'deleted') return;

                // 🎯 [Lifecycle] Lazy Expiration Check
                let expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() : (data.expiresAt ? new Date(data.expiresAt) : null);
                if (!expiresAt && data.createdAt) {
                    const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
                    expiresAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
                }

                if (expiresAt && expiresAt < now) return;

                if (data.geo) {
                    // 🎯 [Normalization]
                    const rawLat = data.geo.lat ?? data.geo.latitude ?? data.geo._lat;
                    const rawLng = data.geo.lng ?? data.geo.longitude ?? data.geo._long;
                    const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
                    const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);

                    if (!isNaN(lat) && !isNaN(lng)) {
                        const distKm = geofire.distanceBetween([lat, lng], centerArr);
                        const distM = distKm * 1000;
                        if (distM <= radiusInMeters) {
                            results.push({
                                id: doc.id,
                                ...data,
                                distanceValue: distM,
                                distance: formatDistance(distM),
                                // 🎯 [Normalization]
                                geo: { lat, lng }
                            });
                        }
                    }
                }
            });
            return results;
        } catch (e) {
            console.error("Legacy Fetch Failed:", e);
            return [];
        }
    },

    /**
     * ONE-TIME MIGRATION UTILITY
     * Updates all existing spots in Firestore with a 'geohash' field.
     */
    migrateExistingSpots: async () => {
        console.log("Starting Geohash Migration...");
        try {
            const snap = await getDocs(collection(db, "spots"));
            let count = 0;
            let failCount = 0;

            for (const d of snap.docs) {
                const data = d.data();
                if (data.geo && !data.geohash) {
                    // Support both Map {lat,lng} and GeoPoint {latitude,longitude}
                    const lat = data.geo.lat || data.geo.latitude;
                    const lng = data.geo.lng || data.geo.longitude;

                    if (lat !== undefined && lng !== undefined) {
                        const hash = geofire.geohashForLocation([lat, lng]);
                        await updateDoc(doc(db, "spots", d.id), { geohash: hash });
                        count++;
                    } else {
                        console.warn(`Spot ${d.id} has invalid coordinates:`, data.geo);
                        failCount++;
                    }
                }
            }
            console.log(`Migration Complete: Updated ${count} spots. Failed ${failCount}.`);
            return { success: true, count, failCount };
        } catch (e) {
            console.error("Migration Failed:", e);
            return { success: false, error: e };
        }
    }
};
